import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ThemeToggle } from '../theme/ThemeToggle';
import { signOut } from '../lib/auth/signOut';
import { MOBILE_OVERFLOW } from './mobileNav';

type MobileOverflowSheetProps = {
  open: boolean;
  onClose: () => void;
};

/** Overflow destinations that are not bottom tabs (Playlists, Schedule, More). */
export function MobileOverflowSheet({ open, onClose }: MobileOverflowSheetProps) {
  const { t } = useTranslation(['layout', 'common']);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] lg:hidden" role="presentation">
      <button
        type="button"
        aria-label={t('nav.closeMenu')}
        className="absolute inset-0 border-0"
        style={{ background: 'color-mix(in srgb, #0a0a0b 45%, transparent)' }}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.more')}
        className="absolute inset-x-0 bottom-0 max-h-[min(70dvh,520px)] overflow-y-auto rounded-t-[16px] border border-b-0 px-3 py-3 shadow-lg"
        style={{
          background: 'var(--gs-panel)',
          borderColor: 'var(--gs-line)',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
        }}
      >
        <div className="mb-2 flex items-center justify-between px-1.5">
          <h2 className="m-0 text-sm font-bold" style={{ color: 'var(--gs-ink)' }}>
            {t('nav.more')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm font-semibold"
            style={{ color: 'var(--gs-muted)' }}
          >
            {t('common:actions.close')}
          </button>
        </div>
        <nav className="flex flex-col gap-0.5" aria-label={t('nav.more')}>
          {MOBILE_OVERFLOW.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                [
                  'rounded-[10px] px-3.5 py-3 text-[15px] font-medium no-underline',
                  isActive ? 'gs-nav-link-active' : '',
                ].join(' ')
              }
              style={{ color: 'var(--gs-ink)' }}
            >
              {t(`nav.items.${item.label}`)}
            </NavLink>
          ))}
        </nav>
        <div
          className="mt-3 flex flex-col gap-3 border-t px-1.5 pt-3"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          <ThemeToggle />
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded-[10px] px-3.5 py-3 text-start text-[15px] font-semibold"
            style={{ color: 'var(--gs-ink)', background: 'var(--gs-panel2)', border: 'none' }}
          >
            {t('common:actions.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}
